import type { Account, AccountLedgerEntry } from '../types';
import { monthKey, normalizeDateISO, nowISO } from './date';

export type TransferEditPatch = {
  fromAccountId?: string;
  toAccountId?: string;
  amount?: number;
  date?: string;
  dateISO?: string;
  note?: string;
  expectedUpdatedAtISO?: string;
};

export type CardPaymentEditPatch = {
  fromAccountId?: string;
  cardAccountId?: string;
  amount?: number;
  date?: string;
  dateISO?: string;
  note?: string;
  expectedUpdatedAtISO?: string;
};

export type NormalizedTransferEdit = {
  fromAccountId: string;
  toAccountId: string;
  amount: number;
  dateISO: string;
  monthKey: string;
  note: string;
};

export type NormalizedCardPaymentEdit = {
  fromAccountId: string;
  cardAccountId: string;
  amount: number;
  dateISO: string;
  monthKey: string;
  note: string;
};

export type AccountDelta = {
  accountId: string;
  amount: number;
};

export function normalizeTransferEdit(originalDebit: AccountLedgerEntry, originalCredit: AccountLedgerEntry, patch: TransferEditPatch): NormalizedTransferEdit {
  const fromAccountId = patch.fromAccountId ?? originalDebit.accountId;
  const toAccountId = patch.toAccountId ?? originalCredit.accountId;
  const amount = Number(patch.amount ?? originalDebit.amount ?? 0);
  const dateISO = normalizeDateISO(patch.dateISO || patch.date || originalDebit.dateISO || originalDebit.date);
  if (!fromAccountId || !toAccountId) throw new Error('Both transfer accounts are required.');
  if (fromAccountId === toAccountId) throw new Error('From and To account cannot be same.');
  if (amount <= 0) throw new Error('Transfer amount must be greater than zero.');
  return {
    fromAccountId,
    toAccountId,
    amount,
    dateISO,
    monthKey: monthKey(dateISO),
    note: patch.note ?? originalDebit.note ?? originalCredit.note ?? ''
  };
}

export function normalizeCardPaymentEdit(originalDebit: AccountLedgerEntry, originalCredit: AccountLedgerEntry, patch: CardPaymentEditPatch): NormalizedCardPaymentEdit {
  const fromAccountId = patch.fromAccountId ?? originalDebit.accountId;
  const cardAccountId = patch.cardAccountId ?? originalCredit.accountId;
  const amount = Number(patch.amount ?? originalDebit.amount ?? 0);
  const dateISO = normalizeDateISO(patch.dateISO || patch.date || originalDebit.dateISO || originalDebit.date);
  if (!fromAccountId || !cardAccountId) throw new Error('Payment source and card are required.');
  if (fromAccountId === cardAccountId) throw new Error('Source and card cannot be same.');
  if (amount <= 0) throw new Error('Payment amount must be greater than zero.');
  return {
    fromAccountId,
    cardAccountId,
    amount,
    dateISO,
    monthKey: monthKey(dateISO),
    note: patch.note ?? originalDebit.note ?? originalCredit.note ?? ''
  };
}

const addDelta = (map: Map<string, number>, accountId: string, delta: number) => {
  map.set(accountId, Number(map.get(accountId) || 0) + delta);
};

export function transferAccountDeltas(originalDebit: AccountLedgerEntry, originalCredit: AccountLedgerEntry, next: NormalizedTransferEdit) {
  const deltas = new Map<string, number>();
  addDelta(deltas, originalDebit.accountId, Number(originalDebit.amount || 0));
  addDelta(deltas, originalCredit.accountId, -Number(originalCredit.amount || 0));
  addDelta(deltas, next.fromAccountId, -next.amount);
  addDelta(deltas, next.toAccountId, next.amount);
  return Array.from(deltas.entries()).map(([accountId, amount]) => ({ accountId, amount })).filter((row) => row.amount !== 0);
}

export function cardPaymentAccountDeltas(originalDebit: AccountLedgerEntry, originalCredit: AccountLedgerEntry, next: NormalizedCardPaymentEdit) {
  const deltas = new Map<string, number>();
  addDelta(deltas, originalDebit.accountId, Number(originalDebit.amount || 0));
  addDelta(deltas, originalCredit.accountId, Number(originalCredit.amount || 0));
  addDelta(deltas, next.fromAccountId, -next.amount);
  addDelta(deltas, next.cardAccountId, -next.amount);
  return Array.from(deltas.entries()).map(([accountId, amount]) => ({ accountId, amount })).filter((row) => row.amount !== 0);
}

export function applyLedgerDelta(account: Account, delta: number) {
  const currentBalance = Number(account.currentBalance || 0) + Number(delta || 0);
  return {
    ...account,
    currentBalance,
    availableLimit: account.type === 'credit_card'
      ? Math.max(Number(account.creditLimit || 0) - currentBalance, 0)
      : Number(account.availableLimit || 0)
  } satisfies Account;
}

export function assertFreshLedgerRows(rows: Pick<AccountLedgerEntry, 'updatedAtISO'>[], expectedUpdatedAtISO?: string) {
  if (!expectedUpdatedAtISO) return;
  const stale = rows.find((row) => row.updatedAtISO && row.updatedAtISO !== expectedUpdatedAtISO);
  if (stale) throw new Error('This linked account event changed since you opened it. Reload and try again.');
}

export function buildLedgerEditAudit(row: AccountLedgerEntry, source: string) {
  return {
    source,
    editedAtISO: nowISO(),
    previous: {
      amount: Number(row.amount || 0),
      dateISO: row.dateISO || row.date,
      accountId: row.accountId || '',
      relatedAccountId: row.relatedAccountId || '',
      note: row.note || '',
      type: row.type,
      direction: row.direction
    }
  };
}

